import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import Bolt from "@mui/icons-material/Bolt";
import ContentCopyOutlined from "@mui/icons-material/ContentCopyOutlined";
import DeleteOutlined from "@mui/icons-material/DeleteOutlined";
import type { ButtonProps } from "@mui/material";
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Skeleton,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import type { NodeKey } from "lexical";
import type { FC } from "react";
import { useCallback } from "react";
import { ChevronDown } from "../../../../../styles/icons/chevron-down";
import { ChevronUp } from "../../../../../styles/icons/chevron-up";
import { useDialog } from "../../../../../utils/hooks/use-dialog";
import type { UUID } from "../../../../../utils/uuid";
import { useLexicalContext } from "../../lexical-context";
import { useLexicalDynamicContentContext } from "../../lexical-dynamic-content-context";
import {
  CLONE_DYNAMIC_CONTENT_COMMAND,
  DELETE_NODE_COMMAND,
  SET_DYNAMIC_CONTENT_OPEN_COMMAND,
} from "../../types/commands";

export const DynamicContentToolbar: FC<{
  segmentId: UUID;
  open: boolean;
  parentKey: NodeKey;
  error?: string;
}> = ({ segmentId, open, parentKey, error }) => {
  const [editor] = useLexicalComposerContext();

  const {
    disabled,
    editorModal: { handleOpen },
  } = useLexicalContext();

  const {
    segmentCounts: { data: segmentCounts, loading },
  } = useLexicalDynamicContentContext();

  const onChange = useCallback(() => {
    handleOpen({
      type: "dynamic-content",
      data: { mode: "edit", segmentId, key: parentKey },
    });
  }, [handleOpen, segmentId, parentKey]);

  const onClone = useCallback(() => {
    editor.dispatchCommand(CLONE_DYNAMIC_CONTENT_COMMAND, parentKey);
  }, [editor, parentKey]);

  const onToggleOpen = useCallback(() => {
    editor.dispatchCommand(SET_DYNAMIC_CONTENT_OPEN_COMMAND, {
      key: parentKey,
      open: !open,
    });
  }, [editor, parentKey, open]);

  const segmentInfo = segmentCounts?.[segmentId];

  return (
    <Stack direction="column" spacing={1}>
      <Stack direction="row" alignItems="center" justifyContent="center" spacing={1} width="100%">
        <Stack
          direction="row"
          alignItems="center"
          p={1}
          m={1}
          borderRadius={1}
          sx={(theme) => ({
            backgroundColor: theme.palette.background.paper,
            border: `1px solid ${theme.palette.divider}`,
          })}
          spacing={1}
        >
          <Tooltip title="Dynamic content">
            <Bolt
              fontSize="small"
              sx={(theme) => ({
                color: error ? theme.palette.error.main : theme.palette.primary.main,
              })}
            />
          </Tooltip>
          {loading ? (
            <Skeleton
              variant="text"
              width={100}
              sx={(theme) => ({ color: theme.palette.primary.main })}
            />
          ) : (
            <Typography
              variant="body2"
              sx={(theme) => ({
                color: error ? theme.palette.error.main : theme.palette.primary.main,
                pb: 0,
              })}
            >
              {segmentInfo?.name ?? "Unnamed segment"}
            </Typography>
          )}
          {loading ? (
            <Skeleton
              variant="text"
              width={20}
              sx={(theme) => ({ color: theme.palette.primary.main })}
            />
          ) : (
            <Tooltip title="Number of recipients in both this segment and the Communication segment">
              <Typography
                variant="body2"
                sx={(theme) => ({
                  color: error ? theme.palette.error.main : theme.palette.primary.main,
                  pb: 0,
                })}
              >
                ({segmentInfo?.count ?? 0})
              </Typography>
            </Tooltip>
          )}
          <Tooltip title="Change targeting">
            <Button
              variant="text"
              size="small"
              onClick={onChange}
              data-analytics-id="edit-dynamic-content"
              disabled={disabled || !!error}
              sx={(theme) => ({ color: theme.palette.primary.main })}
            >
              Change
            </Button>
          </Tooltip>

          <DeleteButton
            sx={(theme) => ({
              color: error ? theme.palette.error.main : theme.palette.primary.main,
            })}
            parentKey={parentKey}
          />
          <Tooltip title="Clone dynamic content block">
            <IconButton
              size="small"
              onClick={onClone}
              data-analytics-id="clone-dynamic-content"
              color="inherit"
              disabled={disabled || !!error}
              sx={(theme) => ({ color: theme.palette.primary.main })}
            >
              <ContentCopyOutlined fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title={open ? "Hide" : "Show"}>
            <IconButton
              size="small"
              onClick={onToggleOpen}
              data-analytics-id="toggle-dynamic-content"
              disabled={!!error}
              sx={(theme) => ({ color: theme.palette.primary.main })}
            >
              {open ? <ChevronUp fontSize="small" /> : <ChevronDown fontSize="small" />}
            </IconButton>
          </Tooltip>
        </Stack>
        {error && (
          <Alert severity="error" sx={{ mt: 1 }}>
            {error}
          </Alert>
        )}
      </Stack>
    </Stack>
  );
};

const DeleteButton: FC<ButtonProps & { parentKey: NodeKey }> = ({ parentKey, ...rest }) => {
  const [editor] = useLexicalComposerContext();
  const { open, handleOpen, handleClose } = useDialog();

  const onDelete = useCallback(() => {
    handleClose();
    editor.dispatchCommand(DELETE_NODE_COMMAND, parentKey);
  }, [editor, parentKey, handleClose]);

  return (
    <>
      <Tooltip title="Delete dynamic content block">
        <IconButton
          size="small"
          onClick={handleOpen}
          data-analytics-id="delete-dynamic-content"
          disabled={false}
          {...rest}
        >
          <DeleteOutlined fontSize="small" />
        </IconButton>
      </Tooltip>
      <Dialog open={open} onClose={handleClose}>
        <DialogTitle>Delete dynamic content?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            Deleting this content means that recipients may not see content targeted to their
            segment, and your content may not make sense any more.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleClose}
            color="primary"
            variant="text"
            data-analytics-id="cancel-dynamic-content-delete"
          >
            Cancel
          </Button>
          <Button
            onClick={onDelete}
            color="error"
            data-analytics-id="confirm-dynamic-content-delete"
            variant="contained"
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
